/**
 * Permission codes the Localisation screens declare an interest in.
 *
 * FSD 1.2.8 "Locations, Geographies and Localisation" covers the location
 * master, the geography hierarchy, country-pack activation/update and this
 * module's three areas — e-invoicing gateway configuration, sandbox
 * pack-validation runs, and statutory document languages. Basic location CRUD
 * already has screens (`web/src/modules/org/LocationsView.vue`,
 * `LocationEditor.vue`, `LocationTree.vue`, `AddressFields.vue`) and is out of
 * this module's scope; this module is the remaining, previously screenless
 * depth.
 *
 * The dot-strings mirror what the server registers with the RBAC catalogue
 * (CLAUDE.md §6) — grep the constants in:
 *  - `server/internal/geotax/einvoicing_config.go`
 *    (`PermEInvoicingRead`/`PermEInvoicingManage`,
 *    `PermSandboxRunRead`/`PermSandboxRunExecute`)
 *  - `server/internal/org/statlang.go`
 *    (`PermStatLangRead`/`PermStatLangManage`)
 *
 * Every route this module declares (`routes.ts`) is gated on the **read**
 * half of its pair, matching `web/src/modules/org/permissions.ts`'s and
 * `web/src/modules/enterprise/reference-sets/permissions.ts`'s convention: a
 * Compliance Officer or Tax Manager holding only read access can open every
 * screen here and change nothing (FSD 1.2.8.1's Compliance Officer / Tax
 * Manager actors).
 */

export const LOCALISATION_PERMISSIONS = {
  /** View e-invoicing gateway configurations (credentials always masked). */
  einvoicingRead: 'geotax.einvoicing.read',
  /**
   * Configure an e-invoicing gateway and run its connectivity test
   * (FND-F08-V07: "a gateway shall pass a connectivity and validation-document
   * test before live submission is enabled").
   */
  einvoicingManage: 'geotax.einvoicing.manage',

  /** View the sandbox pack-validation run history (F-8.008). */
  sandboxRunRead: 'geotax.sandbox_run.read',
  /** Execute a sandbox pack-validation run against a pending tax pack update. */
  sandboxRunExecute: 'geotax.sandbox_run.execute',

  /** View the statutory document-language register (F-8.004). */
  statutoryLanguageRead: 'org.statutory_language.read',
  /**
   * Declare or revise which language a statutory document class renders in
   * for a country.
   */
  statutoryLanguageManage: 'org.statutory_language.manage',
} as const

export type LocalisationPermission =
  (typeof LOCALISATION_PERMISSIONS)[keyof typeof LOCALISATION_PERMISSIONS]
